// ============================================================================
// JARVISTRAVEL - DEMO SESSION IDENTITY
// The per-tab identity a demo session stores its local trips under.
// ============================================================================

import { DEMO_OWNER_PREFIX } from './localTrips';

// Not "jarvis_demo_session": client.ts already owns that exact name in
// localStorage as the boolean demo FLAG ('1'), and reusing it here — same
// name, different store, different value shape — was a conflation waiting for
// the first refactor that reads the wrong store (JAR-704 review round 2).
const DEMO_OWNER_KEY = 'jarvis_demo_owner';

// Fallback identity when sessionStorage is unavailable (Safari private mode,
// storage disabled). Module-scoped so a rehydrate within the same page load
// still lands in the same bucket; a reload gets a fresh one, which loses the
// demo's trips but never hands them to the next visitor.
let inMemoryDemoId: string | null = null;

/** Test seam: forget the in-memory demo id, so one case's demo identity does not
 *  become the next case's returning visitor (JAR-1328). */
export function __resetDemoIdentity(): void {
  inMemoryDemoId = null;
}

// crypto.randomUUID is undefined outside secure contexts (plain-http LAN
// origins) and in older Safari. The catch below must not call the thing that
// threw, and demo identities need uniqueness, not unguessability — the id
// gates nothing.
const mintDemoId = (): string =>
  `${DEMO_OWNER_PREFIX}${
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : Date.now().toString(36) + Math.random().toString(36).slice(2)
  }`;

/**
 * This demo session's identity — per TAB, not per browser.
 *
 * It used to be the constant "demo-1", so every demo session on a browser
 * shared `jarvis_local_trips:demo-1` and the next visitor to open the demo was
 * seeded with the previous visitor's trips whenever the sign-out purge had not
 * run — a crash, a killed tab, the Clerk account switcher. Those are exactly
 * the paths JAR-704 exists for, and JAR-599 already answered this for signed-in
 * users by namespacing rather than by relying on cleanup.
 *
 * sessionStorage is the right lifetime: it survives a reload, which is what
 * JAR-286 needs so a refreshed demo lands back in the same account, and it dies
 * with the tab, which is what makes the next demo a different account.
 *
 * Deliberately NO sweep of other demo buckets here (review round 2):
 * sessionStorage is per-tab, so minting in a new tab while another demo tab is
 * live would delete the live tab's persisted trips out from under it. Abandoned
 * buckets are unreachable (unique ids) and are removed by the existing
 * sign-out/exit-demo purge, which sweeps every jarvis_-prefixed key.
 */
export function demoSessionId(): string {
  try {
    const existing = sessionStorage.getItem(DEMO_OWNER_KEY);
    if (existing) return existing;
    const minted = mintDemoId();
    sessionStorage.setItem(DEMO_OWNER_KEY, minted);
    return minted;
  } catch {
    inMemoryDemoId ??= mintDemoId();
    return inMemoryDemoId;
  }
}
